import { useCallback, useEffect, useRef, useState } from 'react';
import useSWR from 'swr';
import { useNavigate } from 'react-router-dom';
import { useForm, useFieldArray } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import baseService from '../../../../services/base';
import {
 Center,
 Container,
 Box,
 Heading,
 Flex,
 Tabs,
 TabList,
 TabPanels,
 TabPanel,
 useToast,
 Button,
 IconButton,
 Tab,
} from '@chakra-ui/react';
import { certificationEditSchema } from '../../../../core/validations/certification';
import { InputText } from '../../../../components/Input';
import PillarConfigEdit from './PillarConfigEdit';
import { fetchData } from '../../../../services/fetch';
import { CancelModal, SuccessModal } from '../../../../components/Modals';
import { ArrowLeft2, ArrowRight2 } from 'iconsax-react';

export default function CertificationEditForm({ defaultValues, mutate, surveyId }) {
 const toast = useToast();
 const navigate = useNavigate();
 const tabListRef = useRef(null);
 // EG-42: abre na PRIMEIRA aba de pilar. O 1 existia para pular a aba
 // "Configurações gerais", que saiu.
 const [tabIndex, setTabIndex] = useState(0);
 const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
 const [cancelModalIsOpen, setCancelModalIsOpen] = useState(false);
 const [showLeftArrow, setShowLeftArrow] = useState(false);
 const [showRightArrow, setShowRightArrow] = useState(false);

 const checkArrowVisibility = useCallback(() => {
  if (tabListRef.current) {
   const { scrollLeft, scrollWidth, clientWidth } = tabListRef.current;
   setShowLeftArrow(scrollLeft > 0);
   setShowRightArrow(scrollLeft + clientWidth < scrollWidth);
  }
 }, []);

 const scrollTabs = useCallback((direction) => {
  if (tabListRef.current) {
   const scrollAmount = tabListRef.current.clientWidth / 2;
   tabListRef.current.scrollBy({
    left: direction === 'left' ? -scrollAmount : scrollAmount,
    behavior: 'smooth',
   });
  }
 }, []);

 const { data: templates, isLoading: templatesIsLoading } = useSWR(
  `/survey-template/all`,
  fetchData
 );

 const {
  handleSubmit,
  register,
  control,
  setValue,
  trigger,
  formState: { errors, isDirty, isValid, isSubmitting },
 } = useForm({
  defaultValues,
  resolver: yupResolver(certificationEditSchema),
  mode: 'onChange',
 });

 const { fields } = useFieldArray({
  control,
  name: 'form',
 });

 const handleTabsChange = (index) => {
  setTabIndex(index);
 };

 const showErrorToast = (message) => {
  toast({
   title: 'Erro',
   description: message,
   status: 'error',
  });
 };

 const onSubmit = async (values) => {
  const { name, form } = values;
  try {
   if (name !== defaultValues?.name) {
    await baseService.patch(`/survey/name/${surveyId}`, {
     description: name,
    });
   }


   // EG-42/AD-5: o laço que apagava pilares SAIU.
   //
   // Pós-EG-40 o pilar é do CATÁLOGO GLOBAL — a rota DELETE apagava a linha do
   // catálogo (e, no caso de /survey/pillar/:id, todos os temas dela) a partir da
   // tela de UM questionário, tirando o conceito de todos os clientes que o
   // usam. Não é dívida técnica: era perda de dado em produção.
   //
   // E, pela AD-9, remover não existe mais como operação: a classificação da
   // questão dentro do questionário é imutável em existência — tem pilar, pode
   // TROCAR; não tem, não ganha. Apagar do catálogo para "desvincular" nunca foi
   // a mesma coisa. Quem precisa aposentar um pilar faz isso na tela de gestão do
   // catálogo, em /pillars, onde apagar é legítimo.


   for (const pillar of form) {
    const { idPillar } = pillar;

    const defaultPillarData = defaultValues?.form?.find(
     (item) => item?.pillarId === pillar?.pillarId
    );
    let questionsToSend = [];

    if (defaultPillarData) {
     const originalQuestionIds = defaultPillarData?.questions.map(
      (q) => q.id_question_template
     );
     const updatedQuestionIds = pillar?.questions.map((q) => q.id_question_template);

     const removedQuestions = originalQuestionIds
      .filter((id) => !updatedQuestionIds.includes(id))
      .map((id) => ({ id, delete: true }));

     const newQuestions = updatedQuestionIds
      .filter((id) => !originalQuestionIds.includes(id))
      .map((id) => ({ id }));

     questionsToSend = [...newQuestions, ...removedQuestions];
    } else {
     questionsToSend = pillar?.questions.map((q) => q.id_question_template);
    }

    // EG-40: pilar do catálogo global. Texto e imagem saíram — o conteúdo do
    // pilar é global e se edita em /pillars.
    const formValues = {
     id_pillar: idPillar ?? pillar?.pillarId,
     questions: questionsToSend,
    };

    if (pillar?.invisible) {
     await baseService.post(`survey/certificate/${surveyId}/new/pillar`, {
      ...formValues,
     });
    } else {
     await baseService.patch(`survey/certificate/${surveyId}/pillar`, {
      ...formValues,
     });
    }
   }
   setIsSuccessModalOpen(true);
  } catch (error) {
   showErrorToast('Não foi possível salvar o questionário');
  }
 };

 useEffect(() => {
  checkArrowVisibility();
  window.addEventListener('resize', checkArrowVisibility);
  return () => window.removeEventListener('resize', checkArrowVisibility);
 }, [checkArrowVisibility, fields]);

 return (
  <form onSubmit={handleSubmit(onSubmit)}>
   <Center bg='white' borderBottom={['1px solid #E5E5E5', 'unset']}>
    <Container maxW='1280px' p='32px 0px'>
     <Flex align='center' justify='space-between' gap='4'>
      <Box>
       <Heading fontSize='24px' mb='4'>
        Questionário de Certificação
       </Heading>
       <Flex gap='6'>
        <InputText
         {...register('name')}
         error={errors.name}
         placeholder='Digite o nome do questionário'
         autoComplete='off'
         w='400px'
        />
        {/* EG-42: "Possui pilares?" e "Número de pilares" SAÍRAM.
            Decisão do TL: a estrutura vem do template. O rádio já estava
            isDisabled e decidia, sem dizer, o MODELO DE NOTA do certificado
            (cutoff global x por pilar, certificate.service.ts:1764). O "Número de
            pilares" só escondia aba — reduzir de 3 para 1 não tocava em questão
            nenhuma, e não existe implementação segura pós-EG-40: pilar existe
            porque questão aponta pra ele, e a AD-9 proíbe a tela de remover
            classificação. */}
       </Flex>
      </Box>

      <Flex gap='4' justify='end'>
       <Button
        variant='cancel'
        w='133px'
        minW='133px'
        h='59px'
        onClick={() => setCancelModalIsOpen(true)}
       >
        Cancelar
       </Button>

       <Button
        type='submit'
        variant='primary'
        w='129px'
        minW='129px'
        h='59px'
        isLoading={isSubmitting}
        isDisabled={!isValid || !isDirty}
       >
        Finalizar
       </Button>
      </Flex>
     </Flex>
    </Container>
   </Center>

   <Center my={['8px', '30px']} px={{ xl: '86px' }}>
    <Container maxW={'1280px'} p={0} minH='80vh'>
     <Box bg='white' borderRadius='12px' pt='4'>
      <Tabs index={tabIndex} onChange={handleTabsChange}>
       <Flex align='center' position='relative'>
        <IconButton
         icon={<ArrowLeft2 />}
         aria-label='Scroll left'
         onClick={() => scrollTabs('left')}
         variant='ghost'
         position='absolute'
         left={0}
         zIndex={2}
         borderRadius='6px'
         w='48px'
         h='46px'
         bgColor={showLeftArrow ? 'white' : 'transparent'}
         opacity={showLeftArrow ? 1 : 0}
         pointerEvents={showLeftArrow ? 'auto' : 'none'}
         transition='background-color .2s ease-out, opacity .15s ease-out, box-shadow .15s ease-out'
         _hover={{
          bg: 'white',
         }}
        />
        <TabList
         ref={tabListRef}
         p={0}
         border='none'
         overflowX='auto'
         overflowY='hidden'
         css={{
          '&::-webkit-scrollbar': { display: 'none' },
          scrollbarWidth: 'none',
         }}
         onScroll={checkArrowVisibility}
         userSelect='none'
         mx='30px'
        >
         {/* EG-42: a aba "Configurações gerais" SAIU junto com o painel dela.
             Ela continha os seletores de pilar, que respondiam "qual pilar do
             catálogo é o slot N" — e slot de pilar é conceito que a EG-40 apagou:
             não existem slots, existem questões apontando pra pilares. */}
         {fields?.map(
          (field, i) =>
           !field?.hidden && (
            <Tab
             key={field.id}
             bg='white'
             pos='relative'
             top='0'
             w='263px'
             maxW='283px'
             h='40px'
             p='0 16px'
             lineHeight='18.78px'
             borderBottom='2px solid #CCCFD1'
             overflowY='visible'
             color='#5C6066'
             userSelect='none'
             zIndex={1}
             _selected={{
              color: '#292B30',
              fontWeight: 600,
              borderBottom: '3px solid #0073BB',
             }}
             className='tab-certification-border'
            >
             {field?.pillarName}
            </Tab>
           )
         )}
        </TabList>
        <IconButton
         icon={<ArrowRight2 />}
         aria-label='Scroll right'
         onClick={() => scrollTabs('right')}
         variant='ghost'
         position='absolute'
         right={0}
         zIndex={2}
         borderRadius='6px'
         w='48px'
         h='46px'
         bgColor={showRightArrow ? 'white' : 'transparent'}
         opacity={showRightArrow ? 1 : 0}
         pointerEvents={showRightArrow ? 'auto' : 'none'}
         transition='background-color .2s ease-out, opacity .15s ease-out, box-shadow .15s ease-out'
         _hover={{
          bg: 'white',
         }}
        />
       </Flex>
       <TabPanels mt='6' borderTop='2px solid #F5F5FB'>
        {/* EG-42: o painel de Configurações gerais saiu junto com a aba. */}

        {fields?.map(
         (field, index) =>
          field?.pillarName && (
           <TabPanel key={field.id} py='0' px='0'>
            <Heading
             fontSize='20px'
             fontWeight={600}
             lineHeight='23.48px'
             p='6'
             maxH='71px'
             borderBottom='2px solid #F5F5FB'
            >
             {field?.pillarName}
            </Heading>

            <PillarConfigEdit
             index={index}
             field={field}
             control={control}
             register={register}
             trigger={trigger}
             setValue={setValue}
             errors={errors}
             templates={templates}
             templatesIsLoading={templatesIsLoading}
            />
           </TabPanel>
          )
        )}
       </TabPanels>
      </Tabs>
     </Box>

     <CancelModal
      isOpen={cancelModalIsOpen}
      onClose={() => setCancelModalIsOpen(false)}
      title='Deseja mesmo cancelar?'
      content='Ao continuar, todas as informações serão perdidas.'
      isLoading={false}
     />

     <SuccessModal
      isOpen={isSuccessModalOpen}
      onClose={() => {
       setIsSuccessModalOpen(false);
       mutate();
       navigate('/researches');
      }}
      title='Questionário editado com sucesso!'
     />
    </Container>
   </Center>
  </form>
 );
}
